import { CSSProperties, useRef, useState } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import NavigationTabs from './NavigationTabs';
import QuickSimulation, {
  QuickSimulationData,
} from './QuickSimulation/QuickSimulation';
import AdvancedConception, {
  AdvancedConceptionData,
} from './AdvancedConception/AdvancedConception';
import Summary from './Summary/Summary';
import NavigationButtons from './NavigationButtons';
import { Input, Loader } from '@photocarbone/nooco-ui';
import SelectUi from '@mui/material/Select';
import './projectSimulator.scss';
import {
  allTabsVisited,
  areAllMaterialsCompleted,
  formatValueToNumber,
  FormValues,
  getAllMaterialsSums,
  ListValueProps,
  PROJECT_SIMULATOR_TABS,
  projectSimulatorFieldRules,
  selectStyle,
  ValidationRule,
} from './utils';
import { useGetProjectSimulator } from '../../../hooks/useQuery/projectSimulator';
import { type TypologyData } from './QuickSimulation/Typology';
import { handleKeyPress } from '../../shared/utils';
import { useTranslation } from 'react-i18next';
import { MenuItem, Tooltip } from '@material-ui/core';
import PercentIconSVG from '../../../assets/images/projectSimulator/PercentIconSVG';
import { useSelector } from 'react-redux';
import { ProjectSimulatorDTO } from '../../../reducers/projectSimulator';
import isEqual from 'lodash.isequal';

export type MaterialsToComplete = {
  name: string;
  percent: number;
};

function ProjectSimulator() {
  const form = useForm();

  const {
    data: projectData,
    isFetching,
    isPostProjectLoading,
  } = useGetProjectSimulator();
  const isPatched = useSelector((state: ProjectSimulatorDTO) => state.projectSimulator.isPatched);
  const [currentTab, setCurrentTab] = useState(
    PROJECT_SIMULATOR_TABS.QUICK_SIMULATION
  );

  const visitedTabsRef = useRef<PROJECT_SIMULATOR_TABS[]>(isPatched ? allTabsVisited : [currentTab]);

  const handleNavigation = (tab: PROJECT_SIMULATOR_TABS) => {
    setCurrentTab(tab);
    if (!visitedTabsRef.current.includes(tab)) {
      visitedTabsRef.current = [...visitedTabsRef.current, tab];
    }
  };

  const quickSimulation: QuickSimulationData = projectData?.data.ProjectOption.projectSimulatorBody.quickSimulation
  const typology: TypologyData = quickSimulation?.typology;

  const quickSimulationFormValues: QuickSimulationData = {
    general: {
      projectType: form.getValues('projectType'),
    },
    generalConception: {
      meanHeight: Number(form.getValues('meanHeight')),
      numberOfLevels: Number(form.getValues('numberOfLevels')),
      morphology: {
        type: {
          width: Number(form.getValues('width')),
          length: Number(form.getValues('length')),
        },
        value: form.watch('morphology'),
      },
    },
    typology: {
      balconies: Number(form.getValues('balconies')),
      plotArea: Number(form.getValues('plotArea')),
      usefulArea: Number(form.getValues('usefulArea')),
      parking: {
        number: form.getValues('parkingType') === typology?.parking.type.list.NONE.id ? 0 : Number(form.getValues('parkingNumbers')),
        type: {
          list: typology?.parking.type.list,
          value: form.getValues('parkingType'),
        },
      },
      buildingTypeId: typology?.buildingTypeId,
    },
  };

const advancedConception: AdvancedConceptionData = projectData?.data.ProjectOption.projectSimulatorBody.advancedConception
  const glazingValue = form.getValues('facadeTechnics') === advancedConception?.facade.technics.list.STEEL_WALL.id 
  ? advancedConception?.facade.glazing.list.DOUBLE.id
  : Number(form.watch('facadeGlazing'))
  
  const advancedConceptionFormValues: AdvancedConceptionData = {
    buildingStructure: {
      ceiling: {
        fixed: Number(form.getValues('fixedCeiling')),
        removable: Number(form.getValues('removableCeiling')),
      },
      elevators: Number(form.getValues('elevators')),
      floor: {
        fake: form.watch('fakeFloor'),
        hardFloor: Number(form.getValues('hardFloor')),
        softFloor: Number(form.getValues('softFloor')),
      },
      foundationType: advancedConception?.buildingStructure.foundationType,
      framework: {
        wood: Number(form.getValues('wood')),
        concrete: Number(form.getValues('concrete')),
        steel: Number(form.getValues('steel')),
      },
      reinforcedConcreteStructure: form.watch('reinforcedConcreteStructure'),
      roofMaterial: advancedConception?.buildingStructure.roofMaterial,
      slab: {
        list: advancedConception?.buildingStructure.slab.list,
        value: Number(form.watch('slab')),
      },
      stairs: Number(form.getValues('stairs')),
      wallType: {
        separative: Number(form.getValues('separativeWall')),
        distributive: Number(form.getValues('distributiveWall')),
      },
    },
    exploitation: {
      production: {
        list: advancedConception?.exploitation.production.list,
        value: Number(form.watch('productionOfExploitation')),
      },
    },
    facade: {
      glazing: {
        list: advancedConception?.facade.glazing.list,
        value: glazingValue,
      },
      glazingFacadeRatio: Number(form.getValues('glazingFacadeRatio')) * 100,
      occultation: {
        list: advancedConception?.facade.occultation.list,
        value: Number(form.watch('facadeOccultation')),
      },
      roofSkylightArea: Number(form.getValues('roofSkylightArea')),
      sunShaderArea: Number(form.getValues('sunShaderArea')),
      technics: {
        list: advancedConception?.facade.technics.list,
        value: Number(form.watch('facadeTechnics')),
      },
    },
    photovoltaic: {
      panelArea: Number(form.getValues('panelArea')),
    },
  };

  const lastFormValuesUpdated = {
    quickSimulation,
    advancedConception,
    ddv: projectData?.data.ddv,
    refUnit: projectData?.data.refUnit,
  };

  const formValues: FormValues = {
    quickSimulation: quickSimulationFormValues,
    advancedConception: advancedConceptionFormValues,
    ddv: Number(form.getValues('lifespan')),
    refUnit: form.getValues('refUnit'),
  };

  const shouldPatchForm = !isEqual(lastFormValuesUpdated, formValues);

  const frameworkMaterials: MaterialsToComplete[] = [
    {
      name: 'wood',
      percent: Number(form.watch('wood')),
    },
    {
      name: 'concrete',
      percent: Number(form.watch('concrete')),
    },
    {
      name: 'steel',
      percent: Number(form.watch('steel')),
    },
  ];

  const wallTypeMaterials: MaterialsToComplete[] = [
    {
      name: 'separativeWall',
      percent: Number(form.watch('separativeWall')),
    },
    {
      name: 'distributiveWall',
      percent: Number(form.watch('distributiveWall')),
    },
  ];

  const ceilingMaterials: MaterialsToComplete[] = [
    {
      name: 'fixed',
      percent: Number(form.watch('fixedCeiling')),
    },
    {
      name: 'removable',
      percent: Number(form.watch('removableCeiling')),
    },
  ];
  const floorMaterials: MaterialsToComplete[] = [
    {
      name: 'hardFloor',
      percent: Number(form.watch('hardFloor')),
    },
    {
      name: 'softFloor',
      percent: Number(form.watch('softFloor')),
    },
  ];

const allMaterialsSums = getAllMaterialsSums([frameworkMaterials, wallTypeMaterials, ceilingMaterials, floorMaterials])

  if (!projectData || isFetching || isPostProjectLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Loader
          variant="indeterminate"
          disableShrink
          style={{ color: '#73b7ec' }}
          size={40}
          thickness={4}
        />
      </div>
    );
  }

  return (
    <div className="project-simulator-container">
      <NavigationTabs
        currentTab={currentTab}
        handleNavigation={handleNavigation}
        form={form}
        formValues={formValues}
        shouldPatchForm={shouldPatchForm}
        visitedTabs={visitedTabsRef.current}
      />
      {currentTab === PROJECT_SIMULATOR_TABS.QUICK_SIMULATION && (
        <QuickSimulation
          quickSimulation={quickSimulation}
          ddv={projectData?.data.ddv}
          availableUnits={projectData?.data.ProjectType.availableUnits}
          refUnit={projectData?.data.refUnit}
          form={form}
        />
      )}

      {currentTab === PROJECT_SIMULATOR_TABS.ADVANCED_CONCEPTION && (
        <AdvancedConception
          advancedConception={advancedConception}
          frameworkMaterials={frameworkMaterials}
          wallTypeMaterials={wallTypeMaterials}
          ceilingMaterials={ceilingMaterials}
          floorMaterials={floorMaterials}
          form={form}
        />
      )}

      {currentTab === PROJECT_SIMULATOR_TABS.SUMMARY && (
        <Summary
          icProjet={projectData?.data.Ic[1].IcProjet ?? 0}
          area={projectData?.data.area}
          refUnit={form.getValues('refUnit')}
          projectData={projectData.data}
        />
      )}

      <div
        style={{
          marginTop: '10px',
        }}
      />
      <NavigationButtons
        currentTab={currentTab}
        handleNavigation={handleNavigation}
        form={form}
        formValues={formValues}
        shouldPatchForm={shouldPatchForm}
        areAllMaterialsCompleted={areAllMaterialsCompleted(allMaterialsSums)}
      />
    </div>
  );
}

type InputProjectSimulatorProps = {
  form: UseFormReturn;
  name: string;
  defaultValue?: string | number;
  rules?: ValidationRule;
  style: CSSProperties;
  disabled: boolean;
  isPercentageField?: boolean;
  hideValueIfFormNotPatched?: boolean;
};

export function InputProjectSimulator({
  form,
  name,
  defaultValue,
  rules = projectSimulatorFieldRules.default,
  style,
  disabled = false,
  isPercentageField = false,
  hideValueIfFormNotPatched = false,
}: InputProjectSimulatorProps) {
  const isPatched = useSelector((state: ProjectSimulatorDTO) => state.projectSimulator.isPatched);
  return (
    <Controller
      control={form.control}
      name={name}
      rules={rules}
      render={({ field }) => {
        field.value = formatValueToNumber(disabled ? '0' : field.value);
        const valueToDisplay = () => {
          if (isPatched || !hideValueIfFormNotPatched) return defaultValue;
          if (!isPatched && hideValueIfFormNotPatched) return '';
        };
        return (
          <InputFieldController
            form={form}
            field={field}
            name={name}
            defaultValue={valueToDisplay()}
            style={style}
            disabled={disabled}
            isPercentageField={isPercentageField}
          />
        );
      }}
    />
  );
}

function InputFieldController({ field, defaultValue, form, name, style, disabled, isPercentageField}) {
  return (
    <Input
      {...field}
      defaultValue={field.value ?? String(defaultValue)}
      hasDifferentValueStyle={!disabled && field.value !== undefined}
      disabledDebouncing={true}
      style={style}
      onKeyPress={handleKeyPress}
      onBlur={() => form.trigger(name)}
      error={Boolean(form.formState.errors[name])}
      disabled={disabled}
      color={disabled ? "#A5A5A5" : "#789ADE"}
      endIcon={isPercentageField && <PercentIconSVG />}
    />
  );
}

export function SelectProjectSimulator({
  form,
  name,
  listValues,
  disabled = false,
  defaultValue,
  style = { width: '80%' },
}) {
  return (
    <Controller
      control={form.control}
      name={name}
      defaultValue={defaultValue}
      render={({ field }) => (
        <SelectFieldController
          listValues={listValues}
          defaultValue={defaultValue}
          disabled={disabled}
          field={field}
          style={style}
        />
      )}
    />
  );
}

function SelectFieldController({
  field,
  listValues,
  disabled,
  defaultValue,
  style,
}: any) {
  const { t } = useTranslation();

  if (disabled) {
    return field.name !== 'facadeGlazing' ? (
      <Tooltip
        title={t('APP.PROJECT.SIMULATOR.SELECT.UNAVAILABLE.CHOICES.TOOLTIP', {
          defaultValue: "D'autres choix seront bientôt disponibles",
        })}
      >
        <SelectUi
          {...field}
          sx={selectStyle}
          value={defaultValue}
          disabled={true}
          style={style}
        >
          <MenuItem key={listValues.id} value={listValues.id}>
            {t(listValues.phraseKey, { defaultValue: listValues.defaultValue })}
          </MenuItem>
        </SelectUi>
      </Tooltip>
    ) : (
      <SelectUi
      {...field}
      sx={selectStyle}
      value={defaultValue}
      disabled={true}
      style={style}
    >
      <MenuItem key={listValues.id} value={listValues.id}>
        {t(listValues.phraseKey, { defaultValue: listValues.defaultValue })}
      </MenuItem>
    </SelectUi>
    )
  }

  return (
    <SelectUi
      {...field}
      sx={selectStyle}
      value={field.value ?? defaultValue}
      style={style}
    >
      {listValues.map((value: ListValueProps) => (
        <MenuItem key={value.id} value={value.id}>
          {t(value.phraseKey, { defaultValue: value.defaultValue })}
        </MenuItem>
      ))}
    </SelectUi>
  );
}

export default ProjectSimulator;
